import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { getUserByEmail, clearPasswordChangeFlag } from '../firebase/userService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen to Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Fetch user profile data from Firestore
        const profileResult = await getUserByEmail(user.email);

        setCurrentUser({
          email: user.email,
          id: user.uid,
          uid: user.uid
        });

        if (profileResult.success && profileResult.user) {
          setUserProfile(profileResult.user);
        } else {
          setUserProfile(null);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    }, (error) => {
      console.error('Auth state change error:', error);
      setLoading(false); // Set loading to false even on error
    });

    // Fallback timeout in case auth never responds
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 3000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const login = async (email, password) => {
    // Convert email to lowercase for case-insensitive login
    const normalizedEmail = email.toLowerCase();

    try {
      // Try to sign in first
      await signInWithEmailAndPassword(auth, normalizedEmail, password);
      return true;
    } catch (error) {
      // If user doesn't exist in Firebase Auth, check if they exist in Firestore
      if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
        try {
          // Check if user exists in Firestore
          const userResult = await getUserByEmail(normalizedEmail);

          if (userResult.success && userResult.user) {
            // User exists in Firestore, create Firebase Auth account
            // Only allow if they're using the default password
            if (password === 'Debate212') {
              await createUserWithEmailAndPassword(auth, normalizedEmail, password);
              return true;
            } else {
              console.error('Invalid password for first login');
              return false;
            }
          } else {
            // User doesn't exist in Firestore - don't allow login
            console.error('User not authorized');
            return false;
          }
        } catch (createError) {
          console.error('Error creating account:', createError);
          return false;
        }
      }
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updatePassword = async (currentPassword, newPassword) => {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        return { success: false, error: 'No user logged in' };
      }

      // Re-authenticate user before password change (required by Firebase)
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await firebaseUpdatePassword(user, newPassword);

      // Clear the needsPasswordChange flag if it was set
      if (userProfile?.id) {
        await clearPasswordChangeFlag(userProfile.id);
        // Update local state
        setUserProfile({ ...userProfile, needsPasswordChange: false });
      }

      return { success: true };
    } catch (error) {
      console.error('Password update error:', error);

      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        return { success: false, error: 'Current password is incorrect' };
      } else if (error.code === 'auth/weak-password') {
        return { success: false, error: 'New password is too weak' };
      } else {
        return { success: false, error: 'Failed to update password. Please try again.' };
      }
    }
  };

  const value = {
    currentUser,
    userProfile,
    login,
    logout,
    updatePassword,
    isAuthenticated: !!currentUser,
    needsPasswordChange: userProfile?.needsPasswordChange || false
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
